/**
 * The book theme (decisions 16, 17; ADR 2): named settings that drive the preview, EPUB, PDF and DOCX alike,
 * with no custom CSS. Part 05 log, table T, lists every setting and how each output reads it.
 *
 * Two principles shape the fields. Theme settings merge last-writer-wins one field at a time (decision 62),
 * so every combination of individually valid values is a valid theme: no rule relates two fields (T0).
 * And every value has an exact form in every output (ADR 2), so sizes are whole half-points (DOCX's font
 * unit), indents whole tenths of an em (whole twips at every size), line heights whole multiples of 5 %.
 */
import { FONT_IDS } from './fonts.ts';
import type { FontId } from './fonts.ts';
import { booleanField, choiceField, frozen, integerField, parseRecordJson, readFields } from './record.ts';

/** Paragraph alignment. */
export type Alignment = 'justify' | 'left';
/** How a chapter title's letters are set. */
export type TitleStyle = 'normal' | 'uppercase' | 'small-caps';
/** The number shown above each chapter title. */
export type ChapterNumbering = 'none' | 'arabic' | 'roman';
/** What a scene break (`***`) shows as. */
export type SceneBreakStyle = 'asterisks' | 'blank';

/** A book theme. Sizes are in half-points (22 = 11 pt), indents and spacing in tenths of an em of the body size. */
export interface Theme {
  readonly bodyFont: FontId;
  readonly headingFont: FontId;
  /** Body text size, half-points. */
  readonly bodySize: number;
  /** Line height, percent of the body size. */
  readonly lineHeight: number;
  readonly alignment: Alignment;
  readonly hyphenate: boolean;
  /** Indent of a paragraph's first line, tenths of an em. */
  readonly firstLineIndent: number;
  /** Extra space between paragraphs, tenths of an em. */
  readonly paragraphSpacing: number;
  /** Whether a paragraph right after a chapter title, heading or scene break is indented too. */
  readonly indentAfterBreak: boolean;
  /** Indent of a block quote on both sides, tenths of an em. */
  readonly quoteIndent: number;
  /** Chapter title size, half-points. */
  readonly chapterTitleSize: number;
  readonly chapterTitleAlign: 'center' | 'left';
  readonly chapterTitleStyle: TitleStyle;
  readonly chapterNumbering: ChapterNumbering;
  /** Size of a heading inside a chapter (`#` and deeper, decision 35), half-points. */
  readonly subheadingSize: number;
  readonly sceneBreak: SceneBreakStyle;
  /** Footnote text size, half-points. */
  readonly footnoteSize: number;
  /** Whether a paragraph's first or last line may never sit alone on a page. */
  readonly widowControl: boolean;
}

/** The unit an integer setting is counted in. */
export type ThemeUnit = 'half-point' | 'percent' | 'tenth-em';

/** One setting's allowed values: the settings screen builds its controls from these. */
export type ThemeField =
  | { readonly kind: 'integer'; readonly unit: ThemeUnit; readonly min: number; readonly max: number; readonly step: number }
  | { readonly kind: 'choice'; readonly values: readonly string[] }
  | { readonly kind: 'boolean' };

const font = { kind: 'choice', values: FONT_IDS } as const;
const flag = { kind: 'boolean' } as const;
const halfPoints = (min: number, max: number) => ({ kind: 'integer', unit: 'half-point', min, max, step: 1 }) as const;
const tenthsEm = (max: number) => ({ kind: 'integer', unit: 'tenth-em', min: 0, max, step: 1 }) as const;

/**
 * Every setting, in the order a serialised theme lists them (table T). The record reader refuses any key or
 * value not listed here (V3, V7, V10), so adding a setting, widening a range or adding a choice is a
 * book-format change (V14): it ships with a new book-format version under decision 65's migration rule, never
 * as a silent edit, or a device on the previous build could no longer read the book's record.
 */
export const THEME_FIELDS = frozen({
  bodyFont: font,
  headingFont: font,
  bodySize: halfPoints(16, 32),
  lineHeight: { kind: 'integer', unit: 'percent', min: 100, max: 200, step: 5 },
  alignment: { kind: 'choice', values: ['justify', 'left'] },
  hyphenate: flag,
  firstLineIndent: tenthsEm(40),
  paragraphSpacing: tenthsEm(20),
  indentAfterBreak: flag,
  quoteIndent: tenthsEm(40),
  chapterTitleSize: halfPoints(20, 96),
  chapterTitleAlign: { kind: 'choice', values: ['center', 'left'] },
  chapterTitleStyle: { kind: 'choice', values: ['normal', 'uppercase', 'small-caps'] },
  chapterNumbering: { kind: 'choice', values: ['none', 'arabic', 'roman'] },
  subheadingSize: halfPoints(16, 64),
  sceneBreak: { kind: 'choice', values: ['asterisks', 'blank'] },
  footnoteSize: halfPoints(12, 28),
  widowControl: flag,
} as const satisfies Readonly<Record<keyof Theme, ThemeField>>);

/** The settings' names in serialised order. */
export const THEME_KEYS: readonly (keyof Theme)[] = frozen([
  'bodyFont',
  'headingFont',
  'bodySize',
  'lineHeight',
  'alignment',
  'hyphenate',
  'firstLineIndent',
  'paragraphSpacing',
  'indentAfterBreak',
  'quoteIndent',
  'chapterTitleSize',
  'chapterTitleAlign',
  'chapterTitleStyle',
  'chapterNumbering',
  'subheadingSize',
  'sceneBreak',
  'footnoteSize',
  'widowControl',
]);

/** The theme a new book starts with (the "classic" preset). */
export const DEFAULT_THEME: Theme = frozen({
  bodyFont: 'source-serif-4',
  headingFont: 'source-serif-4',
  bodySize: 22,
  lineHeight: 135,
  alignment: 'justify',
  hyphenate: true,
  firstLineIndent: 15,
  paragraphSpacing: 0,
  indentAfterBreak: false,
  quoteIndent: 20,
  chapterTitleSize: 40,
  chapterTitleAlign: 'center',
  chapterTitleStyle: 'normal',
  chapterNumbering: 'none',
  subheadingSize: 26,
  sceneBreak: 'asterisks',
  footnoteSize: 18,
  widowControl: true,
});

/** A book styling preset's ID (decision 16). */
export type PresetId = 'classic' | 'elegant' | 'modern' | 'large-print';

/** The book styling presets (decision 16): whole themes the writer starts from and may then change. */
export const THEME_PRESETS: Readonly<Record<PresetId, Theme>> = frozen({
  classic: DEFAULT_THEME,
  elegant: {
    ...DEFAULT_THEME,
    bodyFont: 'eb-garamond',
    headingFont: 'eb-garamond',
    bodySize: 23,
    lineHeight: 140,
    firstLineIndent: 12,
    chapterTitleSize: 36,
    chapterTitleStyle: 'small-caps',
    chapterNumbering: 'roman',
  },
  modern: {
    ...DEFAULT_THEME,
    bodyFont: 'literata',
    headingFont: 'source-sans-3',
    bodySize: 21,
    lineHeight: 150,
    alignment: 'left',
    hyphenate: false,
    firstLineIndent: 0,
    paragraphSpacing: 8,
    chapterTitleSize: 44,
    chapterTitleAlign: 'left',
    chapterNumbering: 'arabic',
    subheadingSize: 28,
  },
  'large-print': {
    ...DEFAULT_THEME,
    bodyFont: 'pt-serif',
    headingFont: 'pt-serif',
    bodySize: 32,
    lineHeight: 150,
    alignment: 'left',
    hyphenate: false,
    firstLineIndent: 20,
    chapterTitleSize: 56,
    subheadingSize: 40,
    footnoteSize: 28,
  },
});

/** A theme record's value to a {@link Theme}, or a `RecordError` naming the first field that is wrong (table V). */
export function parseTheme(value: unknown): Theme {
  const fields = readFields(value, THEME_KEYS, 'theme');
  const f = THEME_FIELDS;
  const size = (key: 'bodySize' | 'chapterTitleSize' | 'subheadingSize' | 'footnoteSize'): number =>
    integerField(fields, key, f[key].min, f[key].max);
  const tenths = (key: 'firstLineIndent' | 'paragraphSpacing' | 'quoteIndent'): number =>
    integerField(fields, key, f[key].min, f[key].max);
  return {
    bodyFont: choiceField(fields, 'bodyFont', f.bodyFont.values),
    headingFont: choiceField(fields, 'headingFont', f.headingFont.values),
    bodySize: size('bodySize'),
    lineHeight: integerField(fields, 'lineHeight', f.lineHeight.min, f.lineHeight.max, f.lineHeight.step),
    alignment: choiceField(fields, 'alignment', f.alignment.values),
    hyphenate: booleanField(fields, 'hyphenate'),
    firstLineIndent: tenths('firstLineIndent'),
    paragraphSpacing: tenths('paragraphSpacing'),
    indentAfterBreak: booleanField(fields, 'indentAfterBreak'),
    quoteIndent: tenths('quoteIndent'),
    chapterTitleSize: size('chapterTitleSize'),
    chapterTitleAlign: choiceField(fields, 'chapterTitleAlign', f.chapterTitleAlign.values),
    chapterTitleStyle: choiceField(fields, 'chapterTitleStyle', f.chapterTitleStyle.values),
    chapterNumbering: choiceField(fields, 'chapterNumbering', f.chapterNumbering.values),
    subheadingSize: size('subheadingSize'),
    sceneBreak: choiceField(fields, 'sceneBreak', f.sceneBreak.values),
    footnoteSize: size('footnoteSize'),
    widowControl: booleanField(fields, 'widowControl'),
  };
}

/**
 * A theme as JSON, fields in {@link THEME_KEYS} order, so equal themes serialise to equal text. It reads the
 * theme's fields by name into a fresh object (W2: a UI store's proxy, a frozen constant or a class instance
 * all serialise alike), checks that with {@link parseTheme} and throws its `RecordError`, so nothing is
 * written that can't be read back (W1).
 */
export function serializeTheme(theme: Theme): string {
  const copy = Object.fromEntries(THEME_KEYS.map((key) => [key, theme[key]]));
  // parseTheme builds its result in THEME_KEYS order.
  return JSON.stringify(parseTheme(copy));
}

/** Serialised theme text to a {@link Theme}; `RecordError` for anything {@link serializeTheme} can't have written. */
export function themeFromJson(text: string): Theme {
  return parseTheme(parseRecordJson(text, 'theme'));
}
